import { Fragment, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { attendanceService } from "../../../api/attendanceService";
import Badge from "../../../components/ui/Badge";
import SessionTimeline from "../../attendance/components/SessionTimeline";
import { parseDateKey } from "../../../utils/date";
import { formatManilaTime } from "../../../utils/manilaTime";
import { cx } from "../../../utils/classNames";
import { ViewLocationsButton } from "../../attendance/components/DayFlags";
import OvertimeTag from "../../overtime/OvertimeTag";
import {
  DAY_STATUS,
  formatHoursOrDash,
  formatMinutes,
  getDayRowId,
} from "../dtrUtils";
import ui from "../../../components/ui/ui.module.css";
import styles from "../Dtr.module.css";

// Time worked beyond 8 h outside approved overtime (shown, never counted)
const formatExtra = (m) =>
  m >= 60 ? `${Math.floor(m / 60)} h${m % 60 ? ` ${m % 60} m` : ""}` : `${m} m`;

/** One day's check-ins (with their notes), loaded when the row is opened. */
const DaySessions = ({ attendanceId }) => {
  const q = useQuery({
    queryKey: ["me:attendance", "day-sessions", attendanceId],
    queryFn: () => attendanceService.getLocations(attendanceId),
    enabled: !!attendanceId,
    staleTime: 60 * 1000,
  });
  if (q.isLoading) return <span className={ui.muted}>Loading…</span>;
  if (q.isError)
    return <span className="text-danger">Couldn't load the check-ins.</span>;
  const sessions = q.data?.sessions ?? [];
  return sessions.length ? (
    <SessionTimeline sessions={sessions} />
  ) : (
    <span className={ui.muted}>No check-ins this day.</span>
  );
};

/** "On leave · VL (paid)", "Holiday · Bonifacio Day", else the status label. */
const statusText = (day, status) => {
  if (day.status === "ON_LEAVE" && day.leaveType)
    return `${status.label} · ${day.leaveType}${
      day.leavePaid === true
        ? " (paid)"
        : day.leavePaid === false
          ? " (unpaid)"
          : ""
    }`;
  if (day.holiday?.name) return `${status.label} · ${day.holiday.name}`;
  return status.label;
};

/**
 * Every day of a cut-off (My DTR and the HR DTR tab). `days[].date` is a plain
 * "YYYY-MM-DD"; days with a check-in can be expanded to their sessions.
 * renderAction(day): the last column (Fix, Approve / Reject…).
 */
const DtrDaysTable = ({
  days = [],
  showRemarks = false,
  renderAction,
  onViewLocations,
}) => {
  const [open, setOpen] = useState(() => new Set());
  const toggle = (date) =>
    setOpen((prev) => {
      const next = new Set(prev);
      next.has(date) ? next.delete(date) : next.add(date);
      return next;
    });
  const colCount = 8 + (showRemarks ? 1 : 0) + (renderAction ? 1 : 0);

  if (!days.length) {
    return <div className={ui.empty}>No days in this cut-off.</div>;
  }

  return (
    <div className={ui.tableWrapper}>
      <table className={cx(ui.table, styles.daysTable)}>
        <thead>
          <tr>
            <th aria-label="Check-ins"></th>
            <th>Date</th>
            <th>Status</th>
            <th>In</th>
            <th>Out</th>
            <th>Work hrs</th>
            <th>OT hrs</th>
            <th>Flags</th>
            {showRemarks && <th>Remarks</th>}
            {renderAction && <th></th>}
          </tr>
        </thead>
        <tbody>
          {days.map((day) => {
            const date = parseDateKey(day.date);
            const status = DAY_STATUS[day.status] ?? {
              label: day.status,
              tone: "gray",
            };
            const isOpen = open.has(day.date);
            const hasSessions = !!day.attendanceId && !!day.firstCheckIn;
            return (
              <Fragment key={day.date}>
                <tr
                  id={getDayRowId(day.date)}
                  className={cx(
                    day.missingCheckOut && styles.rowWarning,
                    day.future && styles.rowFuture,
                  )}
                >
                  <td className={styles.expandCell}>
                    {hasSessions && (
                      <button
                        type="button"
                        className={ui.iconButton}
                        onClick={() => toggle(day.date)}
                        aria-expanded={isOpen}
                        aria-label={
                          isOpen ? "Hide check-ins" : "Show check-ins"
                        }
                      >
                        <i
                          className={`bi ${isOpen ? "bi-chevron-down" : "bi-chevron-right"}`}
                        ></i>
                      </button>
                    )}
                  </td>
                  <td className={ui.noWrap} data-label="Date">
                    <span className={ui.muted}>
                      {day.weekday ||
                        date.toLocaleDateString("en-PH", { weekday: "short" })}
                    </span>{" "}
                    {date.toLocaleDateString("en-PH", {
                      month: "short",
                      day: "numeric",
                    })}
                  </td>
                  <td data-label="Status">
                    {day.future ? (
                      <span className={ui.muted}>—</span>
                    ) : (
                      <Badge tone={status.tone}>
                        {statusText(day, status)}
                      </Badge>
                    )}
                  </td>
                  <td className={ui.noWrap} data-label="In">
                    {formatManilaTime(day.firstCheckIn)}
                  </td>
                  <td className={ui.noWrap} data-label="Out">
                    {formatManilaTime(day.lastCheckOut)}
                    {onViewLocations && day.attendanceId && (
                      <ViewLocationsButton
                        record={{
                          id: day.attendanceId,
                          hasLocation: !!day.firstCheckIn,
                        }}
                        onClick={onViewLocations}
                        className="ms-2"
                      />
                    )}
                  </td>
                  <td data-label="Work hrs">
                    {formatHoursOrDash(day.workHours)}
                  </td>
                  <td data-label="OT hrs">
                    {formatHoursOrDash(day.overtimeHours)}
                  </td>
                  <td data-label="Flags">
                    <div className={ui.badgeGroup}>
                      {day.isLate && day.status !== "LATE" && (
                        <Badge tone="amber">Late</Badge>
                      )}
                      {day.lateMinutes > 0 && (
                        <Badge tone="amber" icon="bi-alarm">
                          {formatMinutes(day.lateMinutes)} late
                        </Badge>
                      )}
                      {day.undertimeMinutes > 0 && (
                        <Badge tone="amber" icon="bi-box-arrow-left">
                          {formatMinutes(day.undertimeMinutes)} undertime
                        </Badge>
                      )}
                      <OvertimeTag day={day} />
                      {day.pendingOvertime > 0 && (
                        <Badge tone="amber" icon="bi-stopwatch">
                          Overtime waiting
                        </Badge>
                      )}
                      {day.sessionCount > 1 && (
                        <Badge tone="gray" icon="bi-signpost-split">
                          {day.sessionCount} check-ins
                        </Badge>
                      )}
                      {day.isCorrected && (
                        <Badge tone="blue" icon="bi-pencil">
                          Corrected
                        </Badge>
                      )}
                      {day.pendingCorrection && (
                        <Badge tone="amber" icon="bi-hourglass-split">
                          Correction pending
                        </Badge>
                      )}
                      {day.unapprovedExtraMinutes > 0 && (
                        <Badge tone="gray" icon="bi-clock">
                          {formatExtra(day.unapprovedExtraMinutes)} extra, not
                          approved
                        </Badge>
                      )}
                      {day.missingCheckOut && (
                        <Badge tone="red" icon="bi-exclamation-triangle">
                          Missing check-out
                        </Badge>
                      )}
                    </div>
                  </td>
                  {showRemarks && (
                    <td className={ui.truncate}>{day.remarks || "—"}</td>
                  )}
                  {renderAction && (
                    <td className="text-end">{renderAction(day)}</td>
                  )}
                </tr>
                {isOpen && (
                  <tr className={styles.sessionsRow}>
                    <td colSpan={colCount}>
                      <DaySessions attendanceId={day.attendanceId} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default DtrDaysTable;
